/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState, useRef } from 'react';
import Image from '@/components/NextImage';
import styles from './BundleDetailsCard.module.scss';
import { useLabels } from '@/hooks/useLabels';

type MergedItem = {
  productImageUrl: string;
  minQty: number;
  id: string;
};

type Props = {
  dealImageUrl: string | null;
  name: string | null;
  description: string;
  mergedItems: MergedItem[];
};

const labelMap = {
  seeMore: 'lbl_deals_see_more',
  seeLess: 'lbl_deals_see_less',
};

const BundleDetailsCardV2 = ({ description, mergedItems }: Props) => {
  const [showMore, setShowMore] = useState(false);
  const seeLessButtonRef = useRef<HTMLButtonElement>(null);
  const seeMoreButtonRef = useRef<HTMLButtonElement>(null);
  const labels = useLabels(labelMap);

  return (
    <section className={styles.container}>
      <div className={styles.details}>
        <div className={styles.productsContainer}>
          {mergedItems.map(({ productImageUrl, minQty, id }, index) => (
            <div key={id} style={{ display: 'flex', alignItems: 'center' }}>
              <div className={styles.product}>
                <div className={styles.imageContainer}>
                  <Image src={productImageUrl} alt={description} width={50} height={50} />
                  <span className={styles.quantity}>{minQty}</span>
                </div>
              </div>
              {index < mergedItems.length - 1 && <span className={styles.separator}>+</span>}
            </div>
          ))}
        </div>
      </div>
      {!showMore ? (
        <div className={styles.seeDetailsContainer}>
          <button
            ref={seeMoreButtonRef}
            className={styles.button}
            onClick={() => {
              setShowMore(true);
              setTimeout(() => seeLessButtonRef.current?.focus(), 0);
            }}
          >
            {labels.seeMore}
          </button>
        </div>
      ) : (
        <div className={styles.seeDetailsContainer}>
          <p className={styles.name}>{description}</p>
          <button
            ref={seeLessButtonRef}
            className={styles.button}
            onClick={() => {
              setShowMore(false);
              setTimeout(() => seeMoreButtonRef.current?.focus(), 0);
            }}
          >
            {labels.seeLess}
          </button>
        </div>
      )}
    </section>
  );
};

export default BundleDetailsCardV2;
